import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../services/supabaseService';

/**
 * Hook do zarządzania ulubionymi (nieruchomości i najemcy).
 * Obsługuje zapis/odczyt z Supabase, synchronizację z UI.
 * 
 * @param {string} userId - ID zalogowanego użytkownika
 * @returns {object} - Stan i akcje dla ulubionych
 */
export const useFavorites = (userId) => {
  // Walidacja userId - musi być niepustym stringiem UUID
  const validUserId = userId && typeof userId === 'string' && userId !== 'null' && userId.length > 5 ? userId : null;

  // Ulubione nieruchomości
  const [favoriteProperties, setFavoriteProperties] = useState([]);
  const [favoritesCount, setFavoritesCount] = useState(0);

  // Ulubieni najemcy
  const [favoriteTenants, setFavoriteTenants] = useState([]);
  const [favoriteTenantsCount, setFavoriteTenantsCount] = useState(0);

  // --- NIERUCHOMOŚCI ---

  const fetchFavoriteProperties = useCallback(async () => {
    if (!validUserId) return;
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('favorites')
        .eq('id', validUserId)
        .single();

      if (error) throw error;

      const favoriteIds = userData?.favorites || [];
      if (favoriteIds.length === 0) {
        setFavoriteProperties([]);
        setFavoritesCount(0);
        return;
      }

      const { data: properties, error: propError } = await supabase
        .from('properties')
        .select('*')
        .in('id', favoriteIds);

      if (propError) throw propError;

      const existingProps = properties || [];
      const existingIds = new Set(existingProps.map(p => p.id));

      // Wykryj usunięte ogłoszenia — ID w favorites ale brak w bazie
      const deletedPlaceholders = favoriteIds
        .filter(id => !existingIds.has(id))
        .map(id => ({
          id,
          _isDeleted: true,
          title: 'Ogłoszenie usunięte',
          city: '',
          price: 0,
        }));

      // Zachowaj kolejność: istniejące + usunięte na końcu
      const allFavorites = [...existingProps, ...deletedPlaceholders];
      setFavoriteProperties(allFavorites);
      setFavoritesCount(existingProps.length);
    } catch (err) {
      setFavoriteProperties([]);
      setFavoritesCount(0);
    }
  }, [validUserId]);

  const removePropertyFromFavorites = useCallback(async (propertyId) => {
    if (!validUserId) return;
    try {
      const newFavorites = favoriteProperties.filter(p => p.id !== propertyId).map(p => p.id);
      const { error } = await supabase
        .from('users')
        .update({ favorites: newFavorites })
        .eq('id', validUserId);
      if (error) throw error;
      setFavoriteProperties(prev => prev.filter(p => p.id !== propertyId));
    } catch (err) {
    }
  }, [validUserId, favoriteProperties]);

  // --- NAJEMCY ---

  const fetchFavoriteTenants = useCallback(async () => {
    if (!validUserId) return;
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('favorite_tenants')
        .eq('id', validUserId)
        .single();

      if (error) throw error;

      const tenantIds = userData?.favorite_tenants || [];
      // Filtruj null/undefined/"null" z tenantIds
      const filteredTenantIds = tenantIds.filter(id => !!id && id !== 'null');
      if (filteredTenantIds.length === 0) {
        setFavoriteTenants([]);
        setFavoriteTenantsCount(0);
        return;
      }

      const { data: tenants, error: tenantError } = await supabase
        .from('tenant_profiles')
        .select('*')
        .in('id', filteredTenantIds);

      if (tenantError) throw tenantError;
      // Map database fields to component-friendly format (same as tenantsService.getAll)
      const mapped = (tenants || []).map(t => ({
        id: t.id,
        name: t.first_name || '',
        firstName: t.first_name,
        avatar: t.profile_photo_url || '',
        age: t.age,
        profession: t.profession || null,
        city: t.city,
        preferredDistrict: t.preferred_district,
        description: t.description,
        roomsWanted: t.preferred_rooms,
        priceMin: t.price_min,
        priceMax: t.price_max,
        priceRange: t.price_min && t.price_max 
          ? `${t.price_min} - ${t.price_max} zł` 
          : '',
        areaMin: t.area_min,
        areaMax: t.area_max,
        petOwner: t.pet_owner,
        smoker: t.smoker,
        phone: t.phone,
        email: t.email,
        moveInDate: t.move_in_date,
        equipment: Array.isArray(t.equipment) ? t.equipment : [],
        isVerified: t.verified || false,
        profile_photo_url: t.profile_photo_url,
        first_name: t.first_name,
      }));
      setFavoriteTenants(mapped);
      setFavoriteTenantsCount(mapped.length);
    } catch (err) {
      setFavoriteTenants([]);
      setFavoriteTenantsCount(0);
    }
  }, [validUserId]);

  const removeTenantFromFavorites = useCallback(async (tenantId) => {
    if (!validUserId) return;
    try {
      const newFavorites = favoriteTenants.filter(t => t.id !== tenantId).map(t => t.id);
      const { error } = await supabase
        .from('users')
        .update({ favorite_tenants: newFavorites })
        .eq('id', validUserId);
      if (error) throw error;
      setFavoriteTenants(prev => prev.filter(t => t.id !== tenantId));
    } catch (err) {
    }
  }, [validUserId, favoriteTenants]);

  // --- SYNC przy logowaniu ---

  useEffect(() => {
    if (!validUserId) {
      setFavoriteProperties([]);
      setFavoritesCount(0);
      setFavoriteTenants([]);
      setFavoriteTenantsCount(0);
      return;
    }
    fetchFavoriteProperties();
    fetchFavoriteTenants();
  }, [validUserId, fetchFavoriteProperties, fetchFavoriteTenants]);

  // Sync counts
  useEffect(() => {
    setFavoritesCount(favoriteProperties.length);
  }, [favoriteProperties]);

  useEffect(() => {
    setFavoriteTenantsCount(favoriteTenants.length);
  }, [favoriteTenants]);

  return {
    // Nieruchomości
    favoriteProperties,
    favoritesCount,
    setFavoriteProperties,
    setFavoritesCount,
    fetchFavoriteProperties,
    removePropertyFromFavorites,

    // Najemcy
    favoriteTenants,
    favoriteTenantsCount,
    setFavoriteTenants,
    setFavoriteTenantsCount,
    fetchFavoriteTenants,
    removeTenantFromFavorites,
  };
};
